import { memo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useUIV2Safe } from '@/contexts/UIV2Context';
import { useReduceMotion } from '@/hooks/useReduceMotion';
import { cn } from '@/lib/utils';

type ChipStatus = 'success' | 'warning' | 'error' | 'info' | 'default';

interface StatusChipProps {
  status?: ChipStatus;
  children: ReactNode;
  icon?: ReactNode;
  pulse?: boolean;
  className?: string;
}

const statusStyles: Record<ChipStatus, { bg: string; text: string; glow: string }> = {
  success: {
    bg: 'bg-success/15',
    text: 'text-success',
    glow: '0 0 12px hsl(var(--success) / 0.4)',
  },
  warning: {
    bg: 'bg-yellow-500/15',
    text: 'text-yellow-500',
    glow: '0 0 12px hsl(45 100% 50% / 0.4)',
  },
  error: {
    bg: 'bg-destructive/15',
    text: 'text-destructive',
    glow: '0 0 12px hsl(var(--destructive) / 0.4)',
  },
  info: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-500',
    glow: '0 0 12px hsl(210 100% 50% / 0.4)',
  },
  default: {
    bg: 'bg-primary/15',
    text: 'text-primary',
    glow: '0 0 12px hsl(var(--primary) / 0.4)',
  },
};

/**
 * StatusChip - Animated status indicator chip
 * Shows status with optional pulse animation
 */
export const StatusChip = memo(function StatusChip({
  status = 'default',
  children,
  icon,
  pulse = false,
  className,
}: StatusChipProps) {
  const { isLowFx, uiV2Enabled } = useUIV2Safe();
  const reduceMotion = useReduceMotion();
  
  const styles = statusStyles[status];
  const shouldPulse = pulse && !isLowFx && !reduceMotion && uiV2Enabled;
  
  return (
    <motion.div
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
        styles.bg,
        styles.text,
        'border border-current/20',
        uiV2Enabled && !isLowFx && 'backdrop-blur-sm',
        className
      )}
      style={uiV2Enabled && !isLowFx ? { boxShadow: styles.glow } : undefined}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
    >
      {/* Pulse indicator */}
      {shouldPulse && (
        <motion.span
          className="w-2 h-2 rounded-full bg-current"
          animate={{
            opacity: [1, 0.4, 1],
            scale: [1, 0.85, 1],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}
      
      {icon && <span className="flex-shrink-0">{icon}</span>}
      
      <span>{children}</span>
    </motion.div>
  );
});
